import { keyframes } from "@mui/material";

/** Higher or Lower visual language — neon arcade, logo green ↑ / amber-red ↓ */
export const HOL = {
  higher: "#a8e063",
  higherDeep: "#5a9e2f",
  lower: "#ff6b3d",
  lowerDeep: "#c43a1a",
  gold: "#f5d76e",
  ink: "#070708",
  panel: "rgba(16, 16, 18, 0.78)",
  panelBorder: "rgba(255,255,255,0.08)",
  muted: "rgba(255,255,255,0.62)",
  fontDisplay: '"Syne", "Avenir Next", system-ui, sans-serif',
  fontBody: '"DM Sans", "Helvetica Neue", system-ui, sans-serif',
};

export const holFloat = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
`;

export const holPulse = keyframes`
  0%, 100% { opacity: 0.55; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.04); }
`;

export const holShimmer = keyframes`
  0% { background-position: 0% 50%; }
  100% { background-position: 200% 50%; }
`;

export const holFadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const holOrbDrift = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(2%, -3%) scale(1.05); }
  66% { transform: translate(-2%, 2%) scale(0.97); }
`;

/** 5–6 → 3/row (3+2 centered), 7–8 → 4/row, 9–10 → 5/row. Mobile stays at 3. */
export const getHolCardLayout = (n) => {
  const count = Math.max(1, Number(n) || 5);
  const perRowXs = Math.min(3, count);
  const perRow = count <= 6 ? 3 : count <= 8 ? 4 : Math.min(5, count);
  return {
    count,
    perRow: { xs: perRowXs, sm: perRow, md: perRow },
    rows: {
      xs: Math.ceil(count / perRowXs),
      sm: Math.ceil(count / perRow),
      md: Math.ceil(count / perRow),
    },
  };
};

const holCardGapPx = { xs: 10, sm: 16, md: 20 };

/** Slot grows to the largest card that still fits the board; leftover row stays centered. */
export const holCardSlotSx = (n) => {
  const { perRow, rows } = getHolCardLayout(n);
  const widthFor = (bp) => {
    const gap = holCardGapPx[bp];
    const cols = perRow[bp];
    const rowCount = rows[bp];
    const clueReserve = 28;
    return `min(calc((100cqw - ${(cols - 1) * gap}px) / ${cols}), calc((100cqh - ${(rowCount - 1) * gap}px - ${clueReserve}px) / ${rowCount} * 5 / 7))`;
  };
  return {
    flex: "0 0 auto",
    minWidth: 0,
    minHeight: 0,
    width: { xs: widthFor("xs"), sm: widthFor("sm"), md: widthFor("md") },
    height: "auto",
    aspectRatio: "5 / 7",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
  };
};

export const holCardsBoardSx = {
  flex: 1,
  minHeight: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  containerType: "size",
  overflowY: "auto",
  overflowX: "hidden",
  px: { xs: 1, sm: 2, md: 3 },
  py: { xs: 1, sm: 1.5, md: 2 },
  WebkitOverflowScrolling: "touch",
  scrollbarWidth: "thin",
  scrollbarColor: "rgba(255,255,255,0.2) transparent",
  "&::-webkit-scrollbar": { width: 6 },
  "&::-webkit-scrollbar-thumb": {
    background: "rgba(255,255,255,0.18)",
    borderRadius: 8,
  },
};

/** Centered flex wrap so a short last row (e.g. 4, 5) sits in the middle. */
export const holCardsGridSx = () => ({
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "center",
  alignContent: "center",
  alignItems: "center",
  width: "100%",
  height: "100%",
  minHeight: 0,
  mx: "auto",
  gap: { xs: `${holCardGapPx.xs}px`, sm: `${holCardGapPx.sm}px`, md: `${holCardGapPx.md}px` },
});

export const holPageSx = {
  height: "100dvh",
  maxHeight: "100%",
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  position: "relative",
  fontFamily: HOL.fontBody,
  background:
    "radial-gradient(ellipse 70% 45% at 18% 12%, rgba(168,224,99,0.22) 0%, transparent 55%)," +
    "radial-gradient(ellipse 55% 40% at 88% 18%, rgba(255,107,61,0.18) 0%, transparent 50%)," +
    "radial-gradient(ellipse 50% 35% at 70% 88%, rgba(245,215,110,0.12) 0%, transparent 55%)," +
    "radial-gradient(ellipse 40% 30% at 8% 78%, rgba(100,180,255,0.08) 0%, transparent 50%)," +
    "linear-gradient(165deg, #050506 0%, #0c0d10 38%, #0a0a0c 72%, #050506 100%)",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    opacity: 0.4,
    backgroundImage:
      "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.045) 1px, transparent 0)",
    backgroundSize: "28px 28px",
    maskImage:
      "radial-gradient(ellipse 75% 65% at 50% 40%, #000 15%, transparent 78%)",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    width: "55vmax",
    height: "55vmax",
    top: "-18%",
    left: "50%",
    transform: "translateX(-50%)",
    pointerEvents: "none",
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(168,224,99,0.1) 0%, rgba(255,107,61,0.06) 42%, transparent 68%)",
    animation: `${holOrbDrift} 14s ease-in-out infinite`,
    filter: "blur(2px)",
  },
};

export const holAppBarSx = {
  bgcolor: "rgba(7,7,8,0.55)",
  backdropFilter: "blur(16px)",
  borderBottom: "1px solid rgba(255,255,255,0.07)",
  boxShadow: "none",
  flexShrink: 0,
};

export const holModeTileSx = (accent) => ({
  height: "100%",
  cursor: "pointer",
  textAlign: "left",
  p: { xs: 2.5, sm: 3 },
  borderRadius: "20px",
  background: HOL.panel,
  backdropFilter: "blur(18px)",
  border: `1px solid ${HOL.panelBorder}`,
  position: "relative",
  overflow: "hidden",
  transition: "transform 0.35s cubic-bezier(0.22,1,0.36,1), border-color 0.25s, box-shadow 0.35s",
  animation: `${holFadeUp} 0.55s ease both`,
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    background: `linear-gradient(90deg, ${accent}, transparent)`,
  },
  "&:hover": {
    transform: "translateY(-6px)",
    borderColor: `${accent}55`,
    boxShadow: `0 18px 48px rgba(0,0,0,0.45), 0 0 0 1px ${accent}22`,
  },
  "&:active": {
    transform: "translateY(-2px)",
  },
});

export const holHigherBtnSx = {
  flex: { xs: "0 0 auto", sm: "none" },
  py: { xs: 0.75, sm: 1.25 },
  px: { xs: 1.1, sm: 4 },
  minHeight: { xs: 40, sm: 50 },
  minWidth: { xs: 40, sm: 150 },
  width: { xs: 40, sm: "auto" },
  fontFamily: HOL.fontDisplay,
  fontWeight: 800,
  fontSize: { xs: "0.75rem", sm: "1rem" },
  lineHeight: 1,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  borderRadius: { xs: "12px", sm: "14px" },
  color: "#0a1206",
  background: `linear-gradient(160deg, ${HOL.higher} 0%, #7bc94a 55%, ${HOL.higherDeep} 100%)`,
  boxShadow: "0 8px 28px rgba(168,224,99,0.28)",
  touchAction: "manipulation",
  transition: "transform 0.2s, box-shadow 0.2s, filter 0.2s",
  "& .MuiButton-startIcon": {
    m: 0,
    mr: { xs: 0, sm: 1 },
    "& > *:nth-of-type(1)": { fontSize: { xs: 22, sm: 22 } },
  },
  "&:hover": {
    background: `linear-gradient(160deg, #b8f070 0%, ${HOL.higher} 50%, #6bb83a 100%)`,
    boxShadow: "0 12px 36px rgba(168,224,99,0.4)",
    filter: "brightness(1.05)",
  },
  "&:active": { transform: "scale(0.97)" },
  "&.Mui-disabled": {
    background: "rgba(168,224,99,0.22)",
    color: "rgba(255,255,255,0.35)",
    boxShadow: "none",
  },
};

export const holLowerBtnSx = {
  flex: { xs: "0 0 auto", sm: "none" },
  py: { xs: 0.75, sm: 1.25 },
  px: { xs: 1.1, sm: 4 },
  minHeight: { xs: 40, sm: 50 },
  minWidth: { xs: 40, sm: 150 },
  width: { xs: 40, sm: "auto" },
  fontFamily: HOL.fontDisplay,
  fontWeight: 800,
  fontSize: { xs: "0.75rem", sm: "1rem" },
  lineHeight: 1,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  borderRadius: { xs: "12px", sm: "14px" },
  color: "#1a0804",
  background: `linear-gradient(160deg, #ff8a5c 0%, ${HOL.lower} 50%, ${HOL.lowerDeep} 100%)`,
  boxShadow: "0 8px 28px rgba(255,107,61,0.28)",
  touchAction: "manipulation",
  transition: "transform 0.2s, box-shadow 0.2s, filter 0.2s",
  "& .MuiButton-startIcon": {
    m: 0,
    mr: { xs: 0, sm: 1 },
    "& > *:nth-of-type(1)": { fontSize: { xs: 22, sm: 22 } },
  },
  "&:hover": {
    background: `linear-gradient(160deg, #ff9b72 0%, #ff7a4d 50%, #d44822 100%)`,
    boxShadow: "0 12px 36px rgba(255,107,61,0.4)",
    filter: "brightness(1.05)",
  },
  "&:active": { transform: "scale(0.97)" },
  "&.Mui-disabled": {
    background: "rgba(255,107,61,0.22)",
    color: "rgba(255,255,255,0.35)",
    boxShadow: "none",
  },
};

export const holTieBtnSx = {
  flex: { xs: "0 0 auto", sm: "none" },
  py: { xs: 0.75, sm: 1.25 },
  px: { xs: 1.1, sm: 4 },
  minHeight: { xs: 40, sm: 50 },
  minWidth: { xs: 40, sm: 150 },
  width: { xs: 40, sm: "auto" },
  fontFamily: HOL.fontDisplay,
  fontWeight: 800,
  fontSize: { xs: "0.75rem", sm: "1rem" },
  lineHeight: 1,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  borderRadius: { xs: "12px", sm: "14px" },
  color: "#1a1208",
  background: `linear-gradient(160deg, #ffe08a 0%, ${HOL.gold} 50%, #d4a437 100%)`,
  boxShadow: "0 8px 28px rgba(245,215,110,0.28)",
  touchAction: "manipulation",
  transition: "transform 0.2s, box-shadow 0.2s, filter 0.2s",
  "& .MuiButton-startIcon": {
    m: 0,
    mr: { xs: 0, sm: 1 },
    "& > *:nth-of-type(1)": { fontSize: { xs: 22, sm: 22 } },
  },
  "&:hover": {
    background: `linear-gradient(160deg, #fff0a8 0%, #ffe08a 50%, #e8b84a 100%)`,
    boxShadow: "0 12px 36px rgba(245,215,110,0.4)",
    filter: "brightness(1.05)",
  },
  "&:active": { transform: "scale(0.97)" },
  "&.Mui-disabled": {
    background: "rgba(245,215,110,0.22)",
    color: "rgba(255,255,255,0.35)",
    boxShadow: "none",
  },
};

export const holPrimaryBtnSx = {
  py: { xs: 1, sm: 1.25 },
  px: { xs: 2.5, sm: 4.5 },
  minHeight: { xs: 42, sm: 50 },
  fontFamily: HOL.fontDisplay,
  fontWeight: 800,
  fontSize: { xs: "0.9rem", sm: "1rem" },
  letterSpacing: "0.04em",
  textTransform: "none",
  borderRadius: "14px",
  color: "#0a0a0a",
  background: `linear-gradient(135deg, ${HOL.gold} 0%, #e8b84a 100%)`,
  boxShadow: "0 10px 32px rgba(245,215,110,0.28)",
  "&:hover": {
    background: "linear-gradient(135deg, #ffe08a 0%, #f5d76e 100%)",
    boxShadow: "0 14px 40px rgba(245,215,110,0.38)",
  },
};

export const holGhostBtnSx = {
  textTransform: "none",
  fontFamily: HOL.fontBody,
  fontWeight: 600,
  color: HOL.muted,
  borderColor: "rgba(255,255,255,0.14)",
  borderRadius: "12px",
  "&:hover": {
    borderColor: "rgba(255,255,255,0.28)",
    bgcolor: "rgba(255,255,255,0.04)",
    color: "#fff",
  },
};

export const holHudSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: 1.5,
  px: 2,
  py: 1,
  borderRadius: "999px",
  background: "rgba(0,0,0,0.45)",
  border: "1px solid rgba(255,255,255,0.1)",
  backdropFilter: "blur(10px)",
};

export const holControlsBarSx = {
  flexShrink: 0,
  position: "relative",
  zIndex: 2,
  py: { xs: 0.75, sm: 1.5 },
  px: { xs: 1.25, sm: 2 },
  pb: {
    xs: "max(8px, env(safe-area-inset-bottom, 0px))",
    sm: 1.5,
  },
};

export const holClueBtnSx = (active) => ({
  mt: { xs: 0.25, sm: 0.5 },
  py: { xs: 0.1, sm: 0.25 },
  px: { xs: 0.5, sm: 1 },
  minWidth: 0,
  fontSize: { xs: "0.6rem", sm: "0.75rem" },
  fontWeight: 700,
  fontFamily: HOL.fontBody,
  textTransform: "none",
  borderRadius: "10px",
  opacity: active ? 1 : 0.4,
  pointerEvents: active ? "auto" : "none",
  ...(active
    ? {
        bgcolor: "rgba(245,215,110,0.18)",
        color: HOL.gold,
        border: "1px solid rgba(245,215,110,0.45)",
        "&:hover": {
          bgcolor: "rgba(245,215,110,0.28)",
          borderColor: HOL.gold,
        },
      }
    : {
        color: HOL.muted,
        borderColor: "rgba(255,255,255,0.12)",
      }),
});
